import { Component } from '@theme/component';

/**
 * Pixels subtracted from the measured header bottom when setting `--kith-header-offset`
 * (used as drawer `top`). Keep this at 0 so the drawer aligns exactly to the
 * visible bottom edge of the Kith header.
 */
const KITH_DRAWER_TOP_TRIM_PX = 0;

/** Scroll distance before the header becomes eligible to hide/show. */
const KITH_STICKY_THRESHOLD_PX = 16;

/**
 * Custom header component for Kith menu drawer with responsive sticky behavior
 * - Desktop: Always sticky
 * - Mobile: Sticky on scroll up only
 */
class KithHeaderComponent extends Component {
  /**
   * Whether the header has been scrolled offscreen on mobile
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation
   * @constant {number}
   */
  #animationDelay = 280;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {number | null} */
  #metricsRaf = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #lastOffsetPx = null;

  /** @type {number | null} */
  #lastHeightPx = null;

  /** @type {HTMLElement | null} */
  #headerGroup = null;

  /** @type {number | null} Cached border-box height of the header (kept fresh by ResizeObserver, no layout read). */
  #selfHeight = null;

  /** @type {number | null} Cached border-box height of the header group. */
  #groupHeight = null;

  /** @type {boolean} Whether the ResizeObserver is already watching the header group. */
  #headerGroupObserved = false;

  /**
   * The visual sticky surface is the whole Shopify header group: announcement
   * bar + Kith header. Mirror the component state there so CSS can move the
   * complete stack instead of only hiding the inner navigation row.
   */
  #syncHeaderGroupState = () => {
    const headerGroup = this.#headerGroup || this.closest('#header-group') || document.getElementById('header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerGroup = headerGroup;
    if (!this.#headerGroupObserved) {
      this.#resizeObserver.observe(headerGroup);
      this.#headerGroupObserved = true;
    }
    const stickyState = this.dataset.stickyState || 'inactive';
    const scrollDirection = this.dataset.scrollDirection || 'none';
    const isAnimating = this.hasAttribute('data-animating');

    if (headerGroup.dataset.kithStickyState !== stickyState) {
      headerGroup.dataset.kithStickyState = stickyState;
    }
    if (headerGroup.dataset.kithScrollDirection !== scrollDirection) {
      headerGroup.dataset.kithScrollDirection = scrollDirection;
    }
    if (isAnimating && !headerGroup.hasAttribute('data-kith-animating')) {
      headerGroup.setAttribute('data-kith-animating', '');
    } else if (!isAnimating && headerGroup.hasAttribute('data-kith-animating')) {
      headerGroup.removeAttribute('data-kith-animating');
    }
  };

  #setAnimating = (isAnimating) => {
    if (this.#timeout != null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (!isAnimating) {
      this.removeAttribute('data-animating');
      this.#syncHeaderGroupState();
      return;
    }

    this.setAttribute('data-animating', '');
    this.#syncHeaderGroupState();
    this.#timeout = window.setTimeout(() => {
      this.#timeout = null;
      this.removeAttribute('data-animating');
      this.#syncHeaderGroupState();
    }, this.#animationDelay);
  };

  /**
   * Drawer `top` must match the header bar's bottom edge in the viewport.
   * Using height alone skips y-offset (e.g. content above the bar) and integer rounding can leave a 1px gap.
   */
  #updateHeaderMetrics = () => {
    // ResizeObserver supplies both measurements without a synchronous layout
    // read. Wait for its initial delivery instead of falling back to
    // getBoundingClientRect during custom-element connection.
    if (this.#selfHeight == null || this.#groupHeight == null) return;

    const heightPx = Math.round(this.#selfHeight);
    const offsetPx = Math.max(0, this.#groupHeight - KITH_DRAWER_TOP_TRIM_PX);

    if (offsetPx === this.#lastOffsetPx && heightPx === this.#lastHeightPx) return;

    this.#lastOffsetPx = offsetPx;
    this.#lastHeightPx = heightPx;
    document.body.style.setProperty('--kith-header-offset', `${offsetPx}px`);
    document.body.style.setProperty('--kith-drawer-top', `${offsetPx}px`);
    document.body.style.setProperty('--header-height', `${heightPx}px`);
    document.body.style.setProperty('--header-group-height', `${heightPx}px`);
  };

  #scheduleHeaderMetrics = () => {
    if (this.#metricsRaf != null) return;
    this.#metricsRaf = requestAnimationFrame(() => {
      this.#metricsRaf = null;
      this.#updateHeaderMetrics();
    });
  };

  /**
   * Keeps `--header-height` and `--kith-header-offset` aligned with the live layout
   */
  #resizeObserver = new ResizeObserver((entries) => {
    // Read sizes straight from the observer entries (free — no forced reflow) and cache
    // them so scroll-driven sticky transitions never need getBoundingClientRect.
    for (const entry of entries) {
      const box = entry.borderBoxSize && entry.borderBoxSize[0];
      const height = box ? box.blockSize : entry.contentRect.height;
      if (entry.target === this) {
        this.#selfHeight = height;
      } else if (entry.target === this.#headerGroup) {
        this.#groupHeight = height;
      }
    }
    this.#scheduleHeaderMetrics();
  });

  /**
   * Observes the header while scrolling the viewport to track when it's actively sticky
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;
      this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
    }, { threshold: 0 });

    this.#intersectionObserver.observe(this);
  };

  #handleScroll = () => {
    if (this.#scrollRaf != null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#processScroll();
    });
  };

  #processScroll = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = delta < 0;
    const previousStickyState = this.dataset.stickyState || '';
    const previousScrollDirection = this.dataset.scrollDirection || 'none';
    let nextStickyState = previousStickyState;
    let nextScrollDirection = previousScrollDirection;

    // Ignore sub-pixel jitter so tiny deltas don't flip the state.
    if (Math.abs(delta) < 2) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Once the shopper has started scrolling, hide on scroll down and reveal on
    // scroll up. A small fixed threshold is more reliable than measuring the
    // child header row now that the full Shopify header group moves as a stack.
    const pastHeader = scrollTop > KITH_STICKY_THRESHOLD_PX;

    this.#offscreen = pastHeader;

    if (!pastHeader) {
      nextStickyState = 'inactive';
      nextScrollDirection = 'none';
    } else if (isScrollingUp) {
      nextStickyState = 'active';
      nextScrollDirection = 'up';
    } else {
      nextStickyState = 'idle';
      nextScrollDirection = 'down';
    }

    const stickyStateChanged = nextStickyState !== previousStickyState;
    const scrollDirectionChanged = nextScrollDirection !== previousScrollDirection;

    if (!stickyStateChanged && !scrollDirectionChanged) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyStateChanged) {
      this.dataset.stickyState = nextStickyState;
    }

    if (scrollDirectionChanged) {
      this.dataset.scrollDirection = nextScrollDirection;
    }

    this.#setAnimating(pastHeader);
    this.#scheduleHeaderMetrics();

    this.#lastScrollTop = scrollTop;
  };

  #handleViewportChange = () => {
    // Viewport changes don't change the scroll-up-only behavior anymore – just
    // refresh measurements. State is driven entirely by scroll position.
    this.#scheduleHeaderMetrics();
  };

  connectedCallback() {
    super.connectedCallback();
    document.body.classList.add('kith-header-mounted');
    this.#resizeObserver.observe(this);
    this.#observeStickyPosition();
    this.#scheduleHeaderMetrics();
    document.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleViewportChange, { passive: true });

    // Header always starts in its natural top position; scroll events drive
    // subsequent hide (idle) / show (active) transitions on every breakpoint.
    this.dataset.stickyState = 'inactive';
    this.dataset.scrollDirection = 'none';
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#syncHeaderGroupState();
  }



  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.classList.remove('kith-header-mounted');
    this.#resizeObserver.disconnect();
    this.#headerGroupObserved = false;
    this.#selfHeight = null;
    this.#groupHeight = null;
    this.#intersectionObserver?.disconnect();
    if (this.#metricsRaf != null) {
      cancelAnimationFrame(this.#metricsRaf);
      this.#metricsRaf = null;
    }
    if (this.#scrollRaf != null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
    if (this.#timeout != null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    document.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleViewportChange);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--header-group-height', '0px');
    document.body.style.setProperty('--kith-header-offset', '0px');
    document.body.style.removeProperty('--kith-drawer-top');
    this.#headerGroup?.removeAttribute('data-kith-sticky-state');
    this.#headerGroup?.removeAttribute('data-kith-scroll-direction');
    this.#headerGroup?.removeAttribute('data-kith-animating');
    this.#headerGroup = null;
  }
}

customElements.define('kith-header-component', KithHeaderComponent); 